/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

const SUPPORTED = ["fr", "en", "es", "ar"];
const DEFAULT_LANG = "fr";
const STORAGE_KEY = "prenim_lang";

/**
 * Ordre d'essai des fichiers de traduction pour une langue donnee.
 *
 * Un seul `fetch` en echec — fichier absent apres un deploiement incomplet,
 * coupure reseau, 502 du proxy — laissait le site sur son ecran de chargement
 * indefiniment, puisque `translations` restait nul. Plutot que d'afficher une
 * page blanche, on sert la langue la plus proche.
 *
 * L'espagnol et le francais sont deux langues romanes, l'anglais est la langue
 * de travail de Prenim, et le francais est la langue par defaut des deux sites :
 * d'ou ces chaines. Le dernier maillon est toujours une langue deja essayee par
 * personne d'autre, pour que la boucle se termine.
 */
const FALLBACKS = {
  fr: ["en", "es"],
  en: ["fr", "es"],
  es: ["fr", "en"],
  ar: ["fr", "en"],
};

const fallbackChain = (lang) => [lang, ...(FALLBACKS[lang] || ["fr"])];

/**
 * Langue memorisee, ou la langue par defaut.
 *
 * L'acces a `localStorage` est protege : sous une politique de blocage des
 * donnees de site, le simple GETTER leve. Il etait appele sans filet dans
 * l'initialiseur d'etat, donc pendant le rendu du fournisseur : l'exception
 * remontait au-dessus de tout, et le site rendait une page blanche — sans meme
 * atteindre l'ecran d'erreur prevu pour ce genre de cas.
 */
const readStoredLang = () => {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored) ? stored : DEFAULT_LANG;
  } catch (error) {
    console.warn("Langue memorisee illisible.", error);
    return DEFAULT_LANG;
  }
};

/**
 * Memorise la langue choisie. Encapsule parce que trois endroits l'ecrivent :
 * le selecteur, et les deux branches de repli qui doivent corriger un choix
 * devenu impossible a servir.
 */
const persistLang = (lang) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (error) {
    // Navigation privee, stockage desactive : le site marche sans memoire.
    console.warn("Langue non memorisee.", error);
  }
};

/** Pose `lang` et `dir` sur <html> pour la langue effectivement affichee. */
const applyDocumentLang = (lang) => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("lang", lang);
  document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
};

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: null,
  loading: true,
  isRTL: false,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(readStoredLang);
  const [translations, setTranslations] = useState(null);
  const [loading, setLoading] = useState(true);
  // Vrai seulement quand TOUTE la chaine de repli a echoue : le site n'a
  // alors aucun texte a afficher, et doit le dire plutot que tourner.
  const [failed, setFailed] = useState(false);
  // Langue reellement servie, qui peut differer de `lang` si le repli a joue.
  const [servedLang, setServedLang] = useState(DEFAULT_LANG);

  // Ces refs donnent a l'effet la valeur courante sans le faire dependre de
  // `translations` ni de `servedLang` : en dependance, tout chargement reussi
  // relancerait l'effet, donc un nouveau fetch, en boucle.
  const translationsRef = useRef(null);
  const servedLangRef = useRef(DEFAULT_LANG);
  useEffect(() => {
    translationsRef.current = translations;
    servedLangRef.current = servedLang;
  }, [translations, servedLang]);

  const isRTL = lang === "ar";

  useEffect(() => {
    // Le contenu de cette langue est deja charge : c'est le cas juste apres un
    // repli, ou `lang` vient d'etre aligne sur la langue servie. Refaire la
    // requete ferait clignoter le site (contenu, roue, contenu) pour rien.
    if (translationsRef.current && servedLangRef.current === lang) {
      applyDocumentLang(lang);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);

    // `lang` et `dir` sont poses tout de suite : la cesure automatique, sur
    // laquelle repose le texte justifie du site, s'appuie sur `lang`, et rien
    // ne justifie d'attendre une reponse reseau pour la renseigner. Ils sont
    // reposes plus bas si le repli a servi une autre langue.
    applyDocumentLang(lang);

    (async () => {
      let data = null;
      let served = lang;

      for (const candidate of fallbackChain(lang)) {
        try {
          const response = await fetch(`/data/site.${candidate}.json`, { cache: "no-cache" });
          // `fetch` ne rejette pas sur une reponse 404 ou 500 : sans ce test,
          // la page d'erreur du serveur partait dans `json()` et echouait la,
          // avec un message sans rapport.
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          data = await response.json();
          served = candidate;
          break;
        } catch (error) {
          if (candidate === lang) {
            console.warn(`Traductions "${lang}" indisponibles, essai du repli.`, error);
          }
        }
      }

      if (!active) return;

      if (data) {
        setTranslations(data);
        // `lang`/`dir` decrivent la langue REELLEMENT servie : annoncer "es"
        // sur un contenu francais casse la cesure et trompe les lecteurs
        // d'ecran comme les moteurs de recherche.
        applyDocumentLang(served);
        setServedLang(served);
        setFailed(false);

        // Le repli a joue : le contenu affiche n'est pas dans la langue
        // demandee. On aligne la selection dessus, sinon le selecteur annonce
        // « ES » au-dessus d'un texte francais. On remet aussi le choix
        // memorise, sans quoi chaque visite retenterait la langue absente.
        // L'effet se rejoue une fois avec `served`, dont le chargement vient de
        // reussir : la boucle s'arrete la.
        if (served !== lang) {
          persistLang(served);
          setLangState(served);
        }
      } else if (translationsRef.current) {
        // Le site affiche deja une langue : on ne le remplace pas par un ecran
        // d'erreur pour un changement de langue rate. On revient simplement sur
        // la langue servie, sans quoi le selecteur afficherait « ES » au-dessus
        // d'un contenu reste francais.
        console.error(`Traductions indisponibles pour "${lang}" : on reste en "${servedLangRef.current}".`);
        applyDocumentLang(servedLangRef.current);
        persistLang(servedLangRef.current);
        setLangState(servedLangRef.current);
      } else {
        // Premier chargement entierement rate : il n'y a rien a afficher.
        console.error("Aucune traduction n'a pu etre chargee.");
        setFailed(true);
      }
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    persistLang(next);
    setLangState(next);
  }, []);

  const value = useMemo(
    () => ({ lang, setLang, t: translations, loading, failed, isRTL, supported: SUPPORTED }),
    [lang, setLang, translations, loading, failed, isRTL]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);

export const LANG_LABELS = {
  fr: { native: "Français", flag: "fr" },
  en: { native: "English", flag: "gb" },
  es: { native: "Español", flag: "es" },
  ar: { native: "العربية", flag: "sa" },
};
